<template>
  <div class="companions-container">
    <div class="collaboration-description">
      <h2 class="companions-header">Companions</h2>
      <p class="companions-description">Enlist friends and travel companions to join you in coordinating this journey. By
        doing so, everyone will receive
        timely updates about the trip and have access to the same itinerary.</p>
    </div>
    <div>
      <h3 class="companions-list-title">Current Companions</h3>
      <p v-if="!loaded">Loading...</p>
      <div v-if="loaded && companions.length === 0 && invitations.length === 0">
        You haven't added anyone yet. Let's get started by adding your first companion</div>
      <div v-if="loaded" class="companion-list">
        <!-- My trip companions -->
        <div v-for="collab in allCompanions" class="collaborator" :key="collab._id">
          <Companion :companion="collab" :userId="userId" :isOwner="isOwner" :tripId="trip.tripId"
            @collaboratorRemoved="collaboratorRemoved">
          </Companion>
        </div>
      </div>
    </div>
    <div v-if="isOwner" class="invite-form">
      <p class="add-companion-heading">Add by email</p>
      <b-form @submit.prevent="sendInvitation">
        <b-form-group>
          <input type="email" class="form-control" name="email" v-model="form.email" placeholder="Email Address"
            v-bind:disabled="sending" required>
          <b-form-invalid-feedback :state="validEmail()">Please enter a valid email
            address</b-form-invalid-feedback>
          <b-form-invalid-feedback :state="isExistingCollaborator()">Email is already a
            companion!</b-form-invalid-feedback>
        </b-form-group>
        <b-button class="add-email-button" type="submit"
          :disabled="!validEmail() || !isExistingCollaborator() || sending">Invite</b-button>
      </b-form>
      <!-- Typeahead results -->
      <div v-if="filteredSuggestions().length > 0" class="suggestion-list">
        <h3 class="companions-list-title">Previous Companions</h3>
        <Companion v-for="s in filteredSuggestions()" :key="s.email" :companion="{ ...s, type: 'suggestion' }"
          :userId="userId" :tripId="trip.tripId" :isOwner="isOwner" @addCompanion="quickInvite">
        </Companion>
      </div>
    </div>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { mapGetters } from 'vuex';
import Companion from './Companion.vue';

export default {
  name: 'Companions',
  components: {
    Companion,
  },
  props: {
    trip: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      companions: [],
      invitations: [],
      suggestions: [],
      companionList: [],
      formDirty: false,
      loaded: false,
      isOwner: false,
      userEmail: null,
      form: {
        email: '',
      },
      sending: false,
      userId: '',
    };
  },
  computed: {
    ...mapGetters({
      getTripById: 'myTrips/getTripById',
    }),
    allCompanions: {
      get() {
        return [...this.companions, ...this.invitations];
      },
    },
  },
  async created() {
    await this.getCollaborators();
    this.loaded = true;
  },
  watch:
  {
    form:
    {
      handler() {
        this.formDirty = true;
      },
      deep: true,
    },
  },
  methods: {
    formInvalid() {
      return this.formDirty && this.validEmail();
    },
    isExistingCollaborator() {
      if (!this.validEmail()) {
        return true;
      }
      if (this.allCompanions.map(c => c.email).includes(this.form.email)) {
        return false;
      }
      return true;
    },
    validEmail() {
      // eslint-disable-next-line
      let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!this.formDirty) {
        return true;
      }
      if (!this.form.email || this.form.email.length === 0) {
        return true;
      }
      const result = re.test(this.form.email);
      return result;
    },
    hasCollaborators() {
      return this.companions.length > 0 || this.invitations.length > 0;
    },
    hasSuggestions() {
      return this.suggestions.length > 0;
    },
    filteredSuggestions() {
      return this.suggestions.filter(s => this.form.email.length && s.email.indexOf(this.form.email) > -1);
    },
    async getCollaborators() {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      this.userId = await user.username;
      this.userEmail = await user.attributes.email;
      this.trip = await this.getTripById(this.trip.tripId);
      this.isOwner = this.trip.owner === this.userId;
      const headers = {
        Accept: 'application/json',
        Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}`,
      };
      const res = await axios.get(
        `${process.env.VUE_APP_API2}tripCollaborators/${this.trip.tripId}`,
        {
          headers,
        },
      );
      this.companionList = res.data;

      const myCollaborators = await axios.get(
        `${process.env.VUE_APP_API2}myCollaborators`,
        {
          headers,
        },
      );
      const allCollabs = myCollaborators.data;
      // allCollabs.forEach((c) => {
      //   if (!this.companionList.find(cc => cc.email === c.email)) {
      //     this.companionList.push(c);
      //   }
      // });

      const reduced = [...this.companionList, ...allCollabs].reduce((acc, c) => {
        if (acc.companions.find(a => a.email === c.email) || acc.invitations.find(a => a.email === c.email)) {
          return acc;
        }
        if ((c.type === 'owner')) {
          this.owner = c;
          acc.companions.push({ ...c, type: 'owner' });
        } else if ((c.trips || []).includes(this.trip.tripId)) {
          acc.companions.push({ ...c, type: 'collaborator' });
        } else if (this.isOwner && (c.invitations || []).includes(this.trip.tripId)) {
          acc.invitations.push({ ...c, type: 'invitation' });
        } else {
          if (!this.isOwner) {
            return acc;
          }
          if (!acc.suggestions.find(a => a.email === c.email)) {
            acc.suggestions.push(c);
          }
        }
        return acc;
      }, {
        companions: [],
        invitations: [],
        suggestions: [],
      });
      console.log(reduced);
      this.companions = this.isOwner ? reduced.companions.filter(c => c.email !== this.userEmail) : reduced.companions;
      this.invitations = reduced.invitations;
      this.suggestions = reduced.suggestions;
      this.loaded = true;
    },
    async sendInvitation() {
      console.log('sendInvitation');
      await this.sendInvite(null);
    },
    async quickInvite(body) {
      console.log('quickInvite', body);
      await this.sendInvite(body);
    },
    async sendInvite(body) {
      this.sending = true;
      console.log('sendInvite');
      const email = body ? body.email.trim() : this.form.email.trim();
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const res = await axios.post(`${process.env.VUE_APP_API2}inviteCollaborator`, body ? JSON.stringify(body) : {
        email,
        ownerId: this.userId,
        tripId: this.trip.tripId,
      }, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}`,
        },
      }).catch((error) => {
        const message = error.response.data;
        this.$bvToast.toast(`Error sending invitation to ${email}. ${message}`, {
          title: 'Companion List',
          autoHideDelay: 3000,
          appendToast: this.append,
          variant: 'danger',
        });
        this.form.email = '';
        this.formDirty = false;
        this.sending = false;
      });
      if (!res || res.status >= 300) {
        return;
      }
      this.$bvToast.toast(`Invitation Sent to ${email}`, {
        title: 'Companion List',
        autoHideDelay: 3000,
        appendToast: this.append,
      });
      this.form.email = '';
      this.formDirty = false;
      this.sending = false;
      await this.getCollaborators();
    },
    async collaboratorRemoved() {
      console.log('collaboratorRemoved');
      await this.getCollaborators();
    },
  },
};

</script>

<style scoped>
.companions-container {
  display: flex;
  gap: 20px;
  margin: '0 30px';
  flex-direction: column;
  margin-top: 30px;
  width: 95%;
  text-align: left;
  margin-left: 15px;
  margin-right: 15px;
  max-width: 40%;
  min-width: 348px;

  @media only screen and (max-width: 768px) {
    width: 100%;
    /* max-width: 80%; */
  }

  @media only screen and (min-width: 1028px) {
    margin-left: 50px;
    margin-right: 50px;
  }
}

.suggestion-list {
  display: flex;
  width: 100%;
  padding: var(--spacing-spacing-sm, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-radius: var(--border-radius-rounded, 4px);
  border: 1px solid #E7E2E2;
  background: #FFF;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
}

.companions-header {
  font-family: "IdealSans-Bold";
  font-size: 24px;
}

.companions-description {
  /* text-align: left; */
  font-size: 16px;
  line-height: 20px;
}

.companions-list-title {
  font-family: "IdealSans-Bold";
  font-size: 18px;
  margin-bottom: 10px;
}

.profile-picture {
  grid-area: profile-picture;
  width: 80px;
  height: 80px;
  margin-top: -40px;
  border: solid 3px #fff;
  border-radius: 50%;
  background-color: rgb(0, 48, 73);
  font-family: "susa-bold";
  color: #ffffff;
  font-size: 48px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.companion-list {
  display: flex;
  flex-direction: column;
  /* padding: 0 8px; */
  gap: 16px;
}

.invite-form {
  margin-bottom: 30px;

  .add-companion-heading {
    font-family: "IdealSans-Medium";
    font-size: 18px;
  }

  .add-email-button {
    border-radius: 5px;
    border: 1px solid #4E97B2;
    background: #173945;
    color: #F6F4EE;
    font-family: KlinicSlabbold;
    font-size: 19px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    margin-top: 12px;
    padding: 8px 24px;
    /* 22.8px */

    &:hover {
      box-shadow: 0 0 3px #003049;
    }

    &:focus {
      box-shadow: 0 0 3px #003049;
    }
  }
}
</style>
